import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { ArticleService } from '../../services/article.service';
import { GenericTemplateCommonService } from '../../shared/components/template-ai-common/services/generic-template-common.service';
import { ArticleViewDialogComponent } from '../article-view-dialog/article-view-dialog.component';

export interface GenerateArticleDialogData {
  projectId: string;
  /** User’s article title / starting text from Content Creation command bar */
  articleTitle?: string;
  topic?: string;
}

const WORD_COUNTS = [500, 1000, 1500, 2500];
const TONE_OPTIONS = [
  'very casual',
  'somewhat casual',
  'neutral',
  'somewhat formal',
  'very formal'
];

@Component({
  selector: 'app-generate-article-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatAngularModule],
  templateUrl: './generate-article-dialog.component.html',
  styleUrl: './generate-article-dialog.component.scss'
})
export class GenerateArticleDialogComponent implements OnInit {
  projectId: string;
  topicHint = '';

  // Suggestions from API
  suggestedTitles: string[] = [];
  titlesUsedByCompetitors: string[] = [];
  suggestedKeywords: string[] = [];
  suggestedInternalLinks: { label: string; url: string }[] = [];
  suggestedCtas: { label: string; url: string }[] = [];
  suggestedFaqs: { question: string; answer: string }[] = [];

  // Form
  audienceLocation = '';
  selectedTitle = '';
  selectedKeywords: string[] = [];
  wordCount = 1000;
  toneOfVoice = 'neutral';
  selectedInternalLinks: { label: string; url: string }[] = [];
  selectedCtas: { label: string; url: string }[] = [];
  selectedFaqs: { question: string; answer: string }[] = [];
  withAiGeneratedImages = false;

  titleTabIndex = 0; // 0 = Suggested by AI, 1 = Used by Competitors
  suggestLoading = false;
  suggestError: string | null = null;
  generateLoading = false;
  generateError: string | null = null;
  generatedArticleId: string | null = null;
  /** When opened from command bar with text — shown in header and kept as default title */
  initialUserArticleTitle = '';

  readonly wordCountOptions = WORD_COUNTS;
  readonly toneOptions = TONE_OPTIONS;

  /** True while generating or after success – prevents editing dialog form data */
  get formDisabled(): boolean {
    return this.generateLoading || !!this.generatedArticleId;
  }

  constructor(
    public dialogRef: MatDialogRef<GenerateArticleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GenerateArticleDialogData,
    private articleService: ArticleService,
    private templateCommonService: GenericTemplateCommonService,
    private dialog: MatDialog,
    private overlay: Overlay,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.projectId = data.projectId;
    const fromUser = (data.articleTitle || data.topic || '').trim();
    this.initialUserArticleTitle = fromUser;
    if (fromUser) {
      this.topicHint = fromUser;
      this.selectedTitle = fromUser;
    } else if (data.topic) {
      this.topicHint = data.topic;
    }
  }

  ngOnInit(): void {
    this.setAudienceFromProject();
    this.loadSuggestions();
  }

  private setAudienceFromProject(): void {
    const project = this.templateCommonService.getCurrentProjectData();
    if (project?.primary_country) this.audienceLocation = project.primary_country;
    if (project?.language && !this.audienceLocation) this.audienceLocation = project.language;
    this.cdr.markForCheck();
  }

  loadSuggestions(): void {
    this.suggestLoading = true;
    this.suggestError = null;
    this.articleService.suggestConfig({
      project_id: this.projectId,
      topic: this.topicHint || undefined,
      title_hint: this.topicHint || undefined
    }).subscribe({
      next: (res) => {
        this.suggestedTitles = res.suggested_titles || [];
        this.titlesUsedByCompetitors = res.titles_used_by_competitors || [];
        this.suggestedKeywords = [...(res.suggested_keywords || [])];
        this.suggestedInternalLinks = [...(res.suggested_internal_links || [])];
        this.suggestedCtas = [...(res.suggested_ctas || [])];
        this.suggestedFaqs = [...(res.suggested_faqs || [])];
        if (this.initialUserArticleTitle) {
          this.selectedTitle = this.initialUserArticleTitle;
        } else if (this.suggestedTitles.length && !this.selectedTitle) {
          this.selectedTitle = this.suggestedTitles[0];
        }
        if (this.suggestedKeywords.length && this.selectedKeywords.length === 0) this.selectedKeywords = [...this.suggestedKeywords];
        if (this.suggestedInternalLinks.length && this.selectedInternalLinks.length === 0) this.selectedInternalLinks = [...this.suggestedInternalLinks];
        if (this.suggestedCtas.length && this.selectedCtas.length === 0) this.selectedCtas = [...this.suggestedCtas];
        if (this.suggestedFaqs.length && this.selectedFaqs.length === 0) this.selectedFaqs = [...this.suggestedFaqs];
        this.suggestLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.suggestError = err.error?.detail || 'Failed to load suggestions';
        this.suggestLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  pickTitle(title: string): void {
    this.selectedTitle = title;
    this.cdr.markForCheck();
  }

  toggleKeyword(kw: string): void {
    const i = this.selectedKeywords.indexOf(kw);
    if (i >= 0) this.selectedKeywords.splice(i, 1);
    else this.selectedKeywords.push(kw);
    this.cdr.markForCheck();
  }

  isKeywordSelected(kw: string): boolean {
    return this.selectedKeywords.includes(kw);
  }

  toggleInternalLink(link: { label: string; url: string }): void {
    const i = this.selectedInternalLinks.findIndex(l => l.url === link.url);
    if (i >= 0) this.selectedInternalLinks.splice(i, 1);
    else this.selectedInternalLinks.push({ ...link });
    this.cdr.markForCheck();
  }

  isInternalLinkSelected(link: { label: string; url: string }): boolean {
    return this.selectedInternalLinks.some(l => l.url === link.url);
  }

  toggleCta(cta: { label: string; url: string }): void {
    const i = this.selectedCtas.findIndex(c => c.url === cta.url);
    if (i >= 0) this.selectedCtas.splice(i, 1);
    else this.selectedCtas.push({ ...cta });
    this.cdr.markForCheck();
  }

  isCtaSelected(cta: { label: string; url: string }): boolean {
    return this.selectedCtas.some(c => c.url === cta.url);
  }

  toggleFaq(faq: { question: string; answer: string }): void {
    const i = this.selectedFaqs.findIndex(f => f.question === faq.question);
    if (i >= 0) this.selectedFaqs.splice(i, 1);
    else this.selectedFaqs.push({ ...faq });
    this.cdr.markForCheck();
  }

  isFaqSelected(faq: { question: string; answer: string }): boolean {
    return this.selectedFaqs.some(f => f.question === faq.question);
  }

  generate(): void {
    if (!this.selectedTitle?.trim()) {
      this.snackBar.open('Please enter or select a title.', 'Close', { duration: 3000 });
      return;
    }
    this.generateLoading = true;
    this.generateError = null;
    this.articleService.generate({
      project_id: this.projectId,
      audience_location: this.audienceLocation || undefined,
      title: this.selectedTitle.trim(),
      keywords: this.selectedKeywords,
      word_count: this.wordCount,
      tone_of_voice: this.toneOfVoice,
      internal_links: this.selectedInternalLinks,
      ctas: this.selectedCtas,
      faqs: this.selectedFaqs,
      with_ai_generated_images: false // Image generation commented out
    }).subscribe({
      next: (res) => {
        this.generateLoading = false;
        this.generatedArticleId = res.id || null;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.generateError = err.error?.detail || 'Failed to generate article';
        this.generateLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  showArticle(): void {
    if (this.generatedArticleId) {
      this.dialog.open(ArticleViewDialogComponent, {
        width: '80%',
        maxWidth: '900px',
        maxHeight: '95vh',
        data: { articleId: this.generatedArticleId },
        disableClose: true,
        scrollStrategy: this.overlay.scrollStrategies.block()
      });
      this.dialogRef.close({ refreshed: true });
    }
  }

  close(): void {
    this.dialogRef.close(this.generatedArticleId ? { refreshed: true } : undefined);
  }
}
